import type { RemarkTag } from '@app/shared/types/sf-data'

export type CommsError = {
  errors: {
    text?: string[]
    base?: string[]
  }
}

export type CommsErrorResponse = {
  body: CommsError
}

export type CommsErrorComposable = {
  errors: CommsError
  handleErrors: (e: CommsErrorResponse) => void
  hasErrors: (field: 'base' | 'text') => boolean
  clearTextErrors: () => void
}

export type CommunicationData = {
  communication: {
    attachments: Attachment[]
    checkLists: CheckList[]
    checkins: Checkin[]
    checks: CheckLink[]
    comments: Comment[]
    contexts: Context[]
    notes: Remark[]
    todos: Remark[]
    sfAssistantCheckTodos: Remark[]
    users: User[]
    reconciliationProposals: ReconciliationProposal[]
    allRemarkTags: RemarkTag[]
    permissions: { audits: { index: boolean } }
  }
}

export type Attachment = {
  id: number
  filename: string
  url: string
}

export type CheckList = {
  id: number
  ledger_id: number
  text: string
  participant_ids: number[]
  allow_any_user: boolean
  url: string
}

export type Checkin = {
  id: number
  user_id: number | null
  any_user_id: number | null
}

export type CheckLink = {
  id: number
  text: string
  renderedText: string
  checkListId: number
  ledgerPeriodName: string
  ledgerId: number
  companyId: number
  checkinIds: number[]
  followerIds: number[]
  contextType: string
  contextId: number
  commentIds: number[]
  allowedActions: { follow: boolean }
  kind: string
  checkListName: string
  sortTimestamp: number
}

export type Comment = {
  id: number
  allowedActions: { edit: boolean; remove: boolean }
  attachmentIds: number[]
  authorId: number
  botName: string | null
  companyId: number
  createdAt: string
  editedAt: string | null
  renderedText: string
  text: string
  updatedAt: string
}

export type Context = {
  caption: string
  id: number
  name: string
  type: string
  url: string
  workflowReviewUrl?: string
}

export type ReconciliationProposal = {
  id: number
  status: 'open' | 'in_progress' | 'submitted' | 'accepted' | 'rejected'
  user_id: number
  ledger_id: number
  owner_id: number
}

export const RemarkContextTypes = {
  ACCOUNT: 'Account',
  BUDGET: 'BudgetSet',
  CLIENT_MEETING: 'ClientMeeting',
  COMPANY: 'Company',
  COMPANY_WORKFLOW_STATUS: 'CompanyWorkflowStatus',
  DOCUMENT: 'Document',
  RECONCILIATION_TEXT: 'ReconciliationText',
  REPORT: 'Report::Template',
}

export type RemarkContextType =
  typeof RemarkContextTypes[keyof typeof RemarkContextTypes]

export type Remark = {
  attachmentIds: number[]
  authorId: number
  botName: string
  commentIds: number[]
  companyId: number
  contextId: number
  clientMeetingId: number | null
  clientMeetingCompletedAt?: string | null
  contextType: RemarkContextType
  createdAt: string
  bucket: 'open' | 'action' | 'discussed' | null
  done: boolean
  doneAt: string
  doneByName: string
  editedAt: string
  followerIds: number[]
  id: number
  kind: string
  lastCommentedAt: string
  ledgerArchived: boolean
  ledgerId: number
  ledgerPeriodName: string
  liquidInputFieldPath: string
  requestedForOwner: boolean
  renderedText: string
  sortTimestamp: number
  sticky: boolean
  text: string
  textPropertyValue: string
  tagIds: number[]
  todo: boolean
  updatedAt: string
  allowedActions: {
    toggleDone: boolean
    follow: boolean
    pin: boolean
    edit: boolean
    updateTags: boolean
    remove: boolean
  }
  pdfAnnotation?: {
    id: number
    xfdfId: string
    remarkId: number
    documentId: number
    userId: number
  }
  documentFilename?: string
  reportElementId?: number
  reportElementName?: string
}

export type Title = {
  id: number
  content: string
  size: string
  kind: 'title'
}

export type User = {
  id: number
  name: string
  username: string
  external: boolean
}

export type Workflow = {
  id: number
  name: string
  default: boolean
  accountIds: number[]
  reconciliationTextIds: number[]
}

export type BubbleMounter = {
  mount: ({ container }: { container: Document | HTMLElement }) => void
  teardown: () => void
}

export type ReconciliationTemplate = {
  id: number
  name: string
}

export type Export = {
  id: number
  title: string
  url: string
}

export type PermanentCategory = {
  id: number
  title: string
  [key: string]: unknown
}

export type DocumentEntry = {
  id: number | string
  title: string
  link: string
  documentUrl?: string
  category?: PermanentCategory
  [key: string]: unknown
}

export type PermanentEntriesResponse = {
  permanentEntries: DocumentEntry[]
  permanentCategory: PermanentCategory | null
  currentPage: number
  totalPages: number
}
